import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  GripVertical,
  Clock,
  Play,
  Package,
  Truck,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import {
  TimelineConfig,
  getTimelineConfig,
  updateTimelineConfig,
} from "@/integrations/supabase/production-timeline";
import { TimelineModeSwitcher } from "@/components/TimelineModeSwitcher";
import { TimelineMode, getTimelineForMode } from "@/constants/timeline-modes";
import { getDefaultTimelineMode, setDefaultTimelineMode } from "@/integrations/supabase/timeline-config";
import { useAuth } from "@/components/AuthProvider";

const DEFAULT_SYSTEM_STEPS = [
  {
    key: "TECIDO",
    order: 1,
    label: "Tecido",
    description: "Pedido recebido, aguardando compra de tecido",
    color: "blue",
    icon: "Clock",
  },
  {
    key: "CORTE",
    order: 2,
    label: "Corte",
    description: "Trabalho iniciado, corte em andamento",
    color: "blue",
    icon: "Play",
  },
  {
    key: "ESTAMPA",
    order: 3,
    label: "Estampa",
    description: "Etapa de estampa/sublimação em andamento",
    color: "blue",
    icon: "Package",
  },
  {
    key: "COSTURA",
    order: 4,
    label: "Costura",
    description: "Produto aguardando costura/acabamento",
    color: "blue",
    icon: "CheckCircle",
  },
  {
    key: "OFICINA",
    order: 5,
    label: "Oficina",
    description: "Produto enviado para costureira/fornecedor externo",
    color: "blue",
    icon: "Truck",
  },
  {
    key: "PRONTO",
    order: 6,
    label: "Pronto",
    description: "Produto finalizado, pronto para entrega",
    color: "green",
    icon: "CheckCircle2",
  },
];

export function ConfiguracaoTimeline() {
  const navigate = useNavigate();
  const { empresa } = useAuth();
  const [config, setConfig] = useState<TimelineConfig | null>(null);
  const [maxSteps, setMaxSteps] = useState(15);
  const [defaultSteps, setDefaultSteps] = useState<TimelineConfig["default_steps"]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [draggedStep, setDraggedStep] = useState<string | null>(null);
  const [editingStepKey, setEditingStepKey] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<TimelineMode>("confeccao");
  const [modeLoaded, setModeLoaded] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    if (!empresa?.id || modeLoaded) return;
    loadDefaultMode();
  }, [empresa?.id, modeLoaded]);

  const loadDefaultMode = async () => {
    if (!empresa?.id) return;
    const mode = await getDefaultTimelineMode(empresa.id);
    setSelectedMode(mode);
    setModeLoaded(true);
  };

  const loadConfig = async () => {
    try {
      const timelineConfig = await getTimelineConfig();
      setConfig(timelineConfig);
      setMaxSteps(timelineConfig.max_steps || 15);
      setDefaultSteps(
        timelineConfig.default_steps.length > 0
          ? timelineConfig.default_steps
          : DEFAULT_SYSTEM_STEPS
      );
    } catch (error) {
      console.error("Erro ao carregar configuração:", error);
      toast.error("Erro ao carregar configuração");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestoreDefaults = () => {
    if (!confirm("Tem certeza que deseja restaurar as etapas padrão? Isso vai substituir todas as customizações.")) {
      return;
    }

    const timelineSteps = getTimelineForMode(selectedMode);
    const mappedSteps = timelineSteps.map((step) => ({
      ...step,
      color: "blue",
      icon: "CheckCircle",
    }));
    setDefaultSteps(mappedSteps);
    setMaxSteps(15); // Reset para padrão
    toast.success("✅ Etapas restauradas para o padrão!");
  };

  const handleSaveConfig = async () => {
    if (maxSteps < 6 || maxSteps > 50) {
      toast.error("Máximo de etapas deve estar entre 6 e 50");
      return;
    }

    if (defaultSteps.length > maxSteps) {
      toast.error(
        `Número de etapas padrão (${defaultSteps.length}) não pode exceder o máximo permitido (${maxSteps})`
      );
      return;
    }

    setIsSaving(true);
    try {
      const result = await updateTimelineConfig({
        max_steps: maxSteps,
        default_steps: defaultSteps,
      });

      if (result.ok) {
        toast.success("Configuração salva com sucesso!");
      } else {
        toast.error(result.error || "Erro ao salvar configuração");
      }
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error("Erro ao salvar configuração");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddStep = () => {
    const newKey = `CUSTOM_${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    const newStep = {
      key: newKey,
      order: Math.max(...defaultSteps.map((s) => s.order), 0) + 1,
      label: "Nova Etapa",
      description: "",
      color: "blue",
      icon: "CheckCircle",
    };
    setDefaultSteps([...defaultSteps, newStep]);
    setEditingStepKey(newKey);
  };

  const handleRemoveStep = (key: string) => {
    const isDefault = DEFAULT_SYSTEM_STEPS.some((s) => s.key === key);
    if (isDefault) {
      toast.error("Não é possível remover etapas padrão do sistema");
      return;
    }
    setDefaultSteps(defaultSteps.filter((s) => s.key !== key));
  };

  const handleUpdateStep = (
    key: string,
    field: "label" | "description" | "color" | "icon",
    value: string
  ) => {
    setDefaultSteps(
      defaultSteps.map((step) =>
        step.key === key ? { ...step, [field]: value } : step
      )
    );
  };

  const handleDragStart = (key: string) => {
    setDraggedStep(key);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetKey: string) => {
    if (!draggedStep || draggedStep === targetKey) return;

    const draggedIndex = defaultSteps.findIndex((s) => s.key === draggedStep);
    const targetIndex = defaultSteps.findIndex((s) => s.key === targetKey);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newSteps = [...defaultSteps];
    const [draggedStepData] = newSteps.splice(draggedIndex, 1);
    newSteps.splice(targetIndex, 0, draggedStepData);

    // Atualizar ordem
    const reorderedSteps = newSteps.map((step, index) => ({
      ...step,
      order: index + 1,
    }));

    setDefaultSteps(reorderedSteps);
    setDraggedStep(null);
  };

  const handleModeChange = async (mode: TimelineMode) => {
    setSelectedMode(mode);
    const timelineSteps = getTimelineForMode(mode);
    const mappedSteps = timelineSteps.map((step) => ({
      ...step,
      color: "blue",
      icon: "CheckCircle",
    }));
    setDefaultSteps(mappedSteps);

    // Salvar no banco
    if (empresa?.id) {
      const saved = await setDefaultTimelineMode(empresa.id, mode);
      if (saved) {
        toast.success(`✅ Timeline de ${mode} carregada e sincronizada!`);
      } else {
        toast.error("Erro ao sincronizar modo");
      }
    } else {
      toast.success(`Timeline de ${mode} carregada!`);
    }
  };

  if (isLoading) {
    return (
      <div className="px-6 py-6 space-y-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/configuracoes")}
          className="text-purple-600 hover:bg-purple-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <div className="flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/configuracoes")}
          className="mb-2 text-purple-600 hover:bg-purple-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <h1 className="text-3xl font-bold">Timeline de Produção</h1>
        <p className="text-gray-500 mt-1">
          Customize as etapas de produção dos seus pedidos
        </p>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-2">
              <p className="text-sm text-blue-800 font-medium">
                ⚙️ Configuração Global - Afeta Todos os Pedidos
              </p>
              <p className="text-sm text-blue-800">
                As mudanças que você fizer aqui se aplicam a <strong>todos os pedidos da sua empresa</strong>. Configure o número máximo de etapas (6 a 50) e customize as etapas padrão que aparecem em novos pedidos.
              </p>
              <p className="text-sm text-blue-700 border-l-2 border-blue-400 pl-3">
                💡 <strong>Personalização por Pedido:</strong> Além disso, cada pedido individual pode ter etapas customizadas específicas adicionadas diretamente do detalhe do pedido, sem afetar as etapas padrão da empresa.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline Mode Switcher */}
      <TimelineModeSwitcher selectedMode={selectedMode} onModeChange={handleModeChange} redirectOnCustom={false} />

      {/* Máximo de Etapas */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Número Máximo de Etapas</CardTitle>
          <CardDescription>
            Define quantas etapas podem ser usadas em cada pedido
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-xs">
            <Label htmlFor="max-steps" className="text-sm">
              Máximo de Etapas
            </Label>
            <Input
              id="max-steps"
              type="number"
              min="6"
              max="50"
              value={maxSteps}
              onChange={(e) => setMaxSteps(parseInt(e.target.value) || 15)}
              className="mt-2"
            />
            <p className="text-xs text-gray-500 mt-2">
              Mínimo: 6 | Máximo: 50 | Padrão: 15
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Etapas Padrão */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Etapas Padrão</CardTitle>
              <CardDescription>
                Arraste para reordenar. Customize labels e descrições.
              </CardDescription>
            </div>
            <Button
              size="sm"
              onClick={handleAddStep}
              disabled={defaultSteps.length >= maxSteps}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Etapa
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {defaultSteps.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              Nenhuma etapa configurada
            </div>
          ) : (
            defaultSteps.map((step, index) => {
              const isDefault = DEFAULT_SYSTEM_STEPS.some(
                (s) => s.key === step.key
              );
              const isEditing = editingStepKey === step.key;

              return (
                <Card
                  key={step.key}
                  draggable
                  onDragStart={() => handleDragStart(step.key)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(step.key)}
                  className={`p-4 cursor-move transition ${
                    draggedStep === step.key ? "opacity-50" : ""
                  }`}
                >
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="flex items-start gap-3">
                      <GripVertical className="w-5 h-5 text-gray-400 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {index + 1}
                          </span>
                          {isDefault && (
                            <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded">
                              Padrão
                            </span>
                          )}
                        </div>
                      </div>
                      {!isDefault && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemoveStep(step.key)}
                          className="text-red-600 hover:text-red-700 h-auto p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    {/* Campos Editáveis */}
                    <div className="space-y-3 ml-8">
                      <div>
                        <Label htmlFor={`label-${step.key}`} className="text-sm">
                          Rótulo da Etapa
                        </Label>
                        <Input
                          id={`label-${step.key}`}
                          value={step.label}
                          onChange={(e) =>
                            handleUpdateStep(step.key, "label", e.target.value)
                          }
                          placeholder="Ex: Corte, Costura..."
                          className="mt-1 text-sm"
                        />
                      </div>

                      <div>
                        <Label
                          htmlFor={`description-${step.key}`}
                          className="text-sm"
                        >
                          Descrição
                        </Label>
                        <Textarea
                          id={`description-${step.key}`}
                          value={step.description}
                          onChange={(e) =>
                            handleUpdateStep(
                              step.key,
                              "description",
                              e.target.value
                            )
                          }
                          placeholder="Descrição opcional dessa etapa..."
                          className="mt-1 text-sm"
                          rows={2}
                        />
                      </div>

                      <div>
                        <Label htmlFor={`color-${step.key}`} className="text-sm">
                          Cor
                        </Label>
                        <select
                          id={`color-${step.key}`}
                          value={step.color || "blue"}
                          onChange={(e) =>
                            handleUpdateStep(step.key, "color", e.target.value)
                          }
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="blue">Azul</option>
                          <option value="green">Verde</option>
                          <option value="yellow">Amarelo</option>
                          <option value="red">Vermelho</option>
                          <option value="purple">Roxo</option>
                          <option value="gray">Cinza</option>
                        </select>
                      </div>

                      <div>
                        <Label className="text-sm">Ícone</Label>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {[
                            { name: "Clock", icon: Clock },
                            { name: "Play", icon: Play },
                            { name: "Package", icon: Package },
                            { name: "CheckCircle", icon: CheckCircle },
                            { name: "Truck", icon: Truck },
                            { name: "CheckCircle2", icon: CheckCircle2 },
                          ].map(({ name, icon: IconComponent }) => (
                            <button
                              key={name}
                              onClick={() =>
                                handleUpdateStep(step.key, "icon", name)
                              }
                              className={`p-2 rounded-lg border-2 transition ${
                                (step.icon || "CheckCircle") === name
                                  ? "border-purple-500 bg-purple-100"
                                  : "border-gray-300 bg-white hover:border-purple-300"
                              }`}
                              title={name}
                            >
                              <IconComponent className="w-5 h-5 text-gray-700" />
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })
          )}

          {defaultSteps.length >= maxSteps && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>Número máximo de etapas atingido</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resumo */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-green-800">
              <p className="font-semibold mb-1">Configuração Atual:</p>
              <ul className="space-y-1">
                <li>
                  • <strong>Máximo de etapas:</strong> {maxSteps}
                </li>
                <li>
                  • <strong>Etapas configuradas:</strong> {defaultSteps.length}
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Botões de Ação */}
      <div className="flex gap-3">
        <Button
          onClick={handleSaveConfig}
          disabled={isSaving}
          className="bg-purple-600 hover:bg-purple-700"
        >
          {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          <Save className="w-4 h-4 mr-2" />
          Salvar Configuração
        </Button>
        <Button
          onClick={handleRestoreDefaults}
          variant="outline"
          className="border-blue-200 text-blue-600 hover:bg-blue-50"
        >
          ↺ Restaurar Padrões
        </Button>
        <Button
          onClick={() => navigate("/configuracoes")}
          variant="outline"
          className="border-purple-200 text-purple-600 hover:bg-purple-50"
        >
          Cancelar
        </Button>
      </div>
    </div>
  );
}
